<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import _ from 'lodash';
import { useStore } from 'vuex';
import { useNotification } from '@kyvg/vue3-notification';
import StreamService from '@/services/StreamService';
import PaywallService from '@/services/PaywallService';
import VODFolderCard from './VODFolderCard.vue';
import FormButton from '../Atoms/FormButton.vue';
import FormInput from '../Atoms/FormInput.vue';
import TabGroup from '../Atoms/TabGroup.vue';
import TabItem from '../Atoms/TabItem.vue';
import DropDownMenu from '../Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue';
import IconBase from '../icon/IconBase.vue';
import IconGrid from '../icon/IconGrid.vue';
import IconList from '../icon/IconList.vue';
import IconPlus from '../icon/IconPlus.vue';
import IconUpload from '../icon/IconUpload.vue';
import IconSearch from '../icon/IconSearch.vue';
import IconSorting from '../icon/IconSorting.vue';
import IconCross from '../icon/IconCross.vue';
import IconChevronUpDown from '../icon/IconChevronUpDown.vue';
import IconCheckmark from '../icon/IconCheckmark.vue';
import VODStorageStat from './VODStorageStat.vue';
import Pagination from '@/components/ui/Pagination.vue';
import VODReachLimitBanner from './VODReachLimitBanner.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import { folderTint, folderInitial } from '@/Utils/folderColor';
import { useFavorites } from '@/Utils/useFavorites';
import { useRecentFolders } from '@/Utils/useRecentFolders';
import { track, EVENTS } from '@/lib/tracking';
import { usePersistedSort } from '@/Utils/usePersistedSort';

const props = defineProps({
	folders: { type: Array, default: () => [] },
	storageUsedBytes: { type: Number, default: 0 },
	maxStorageGb: { type: Number, default: 0 },
	canManage: { type: Boolean, default: true },
	disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['new-folder', 'refresh']);

const store = useStore();
const router = useRouter();
const { notify } = useNotification();
const { isFavorite } = useFavorites();

const VIEW_KEY = 'castr.vod.folderView';
const FOLDER_SORT = ['Newest', 'Oldest', 'A to Z', 'Z to A', 'Most videos'];
const TYPE_KEY = 'castr.vod.folderType';
const view = ref(localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'card');
const setView = (v) => { view.value = v; localStorage.setItem(VIEW_KEY, v); };

const sortDropdown = ref(null);
const sortOptions = FOLDER_SORT;
const { sortBy, setSort } = usePersistedSort('castr.vod.folderSort', FOLDER_SORT, 'Newest');
const changeSort = (value) => { setSort(value); sortDropdown.value?.toggleDropdown(); };

const typeFilter = ref(['all', 'normal', 'live'].includes(localStorage.getItem(TYPE_KEY)) ? localStorage.getItem(TYPE_KEY) : 'all');
// Segmented filter (Vimeo/Frame.io pattern): visible one-click scopes with counts.
const typeTabs = computed(() => [
	{ value: 'all', label: 'All', count: props.folders.length },
	{ value: 'normal', label: 'Folders', count: props.folders.filter((f) => !f.liveParent).length },
	{ value: 'live', label: 'Live recordings', count: props.folders.filter((f) => !!f.liveParent).length },
]);
const changeType = (value) => { typeFilter.value = value; localStorage.setItem(TYPE_KEY, value); };

const searchQuery = ref('');

// Type filter + sort + search only add value once there's a meaningful list.
const showListControls = computed(() => props.folders.length > 2);

// Storage sits beside the title on mobile and with the actions on sm+.
const smUp = ref(typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches);
let smMq;
const syncSmUp = () => { smUp.value = smMq.matches; };
onMounted(() => {
	smMq = window.matchMedia('(min-width: 640px)');
	syncSmUp();
	smMq.addEventListener('change', syncSmUp);
});
onBeforeUnmount(() => {
	smMq?.removeEventListener('change', syncSmUp);
});

const onNewVideo = (folder, source = 'computer') => {
	// Push the videos child directly so `?new=computer` is not dropped by the
	// parent route's redirect to VODBucketVideos.
	router.push({ name: 'VODBucketVideos', params: { streamId: folder._id }, query: { new: source } });
};

// Live-to-VOD folders are filled by recordings, not manual uploads, so they are
// never valid upload targets.
const uploadTargets = computed(() => props.folders.filter((f) => !f.liveParent));

// Upload video: one folder → go straight there; several → anchored picker.
const uploadDropdown = ref(null);
const uploadSearch = ref('');
const uploadSearchInput = ref(null);
const uploadSortOptions = ['Newest', 'Oldest', 'A to Z', 'Z to A', 'Most videos'];
const UPLOAD_SORT_KEY = 'castr.vod.uploadFolderSort';
const uploadSort = ref(uploadSortOptions.includes(localStorage.getItem(UPLOAD_SORT_KEY)) ? localStorage.getItem(UPLOAD_SORT_KEY) : 'Newest');
const uploadSortOpen = ref(false);
// The dot marks a change made in this visit. A saved sort must not light it on open.
const uploadSortChanged = ref(false);
const uploadSortRoot = ref(null);
const uploadSortMenu = ref(null);
const sortUploadTargets = (list) => {
	switch (uploadSort.value) {
		case 'Oldest':
			return _.orderBy(list, [(f) => new Date(f.creationTime)], ['asc']);
		case 'A to Z':
			return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['asc']);
		case 'Z to A':
			return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['desc']);
		case 'Most videos':
			return _.orderBy(list, [(f) => f.totalFiles || 0, (f) => new Date(f.creationTime)], ['desc', 'desc']);
		case 'Newest':
		default:
			return _.orderBy(list, [(f) => new Date(f.creationTime)], ['desc']);
	}
};
const filteredUploadTargets = computed(() => {
	const query = uploadSearch.value.trim().toLowerCase();
	const list = query
		? uploadTargets.value.filter((folder) => (folder?.name || '').toLowerCase().includes(query))
		: uploadTargets.value;
	return sortUploadTargets(list);
});
const onUploadClick = () => {
	if (uploadTargets.value.length === 1) {
		onNewVideo(uploadTargets.value[0], 'computer');
	}
};
const uploadSortPos = ref({ top: 0, left: 0 });
const placeUploadSort = () => {
	const rect = uploadSortRoot.value?.getBoundingClientRect();
	if (!rect) return;
	const width = 168;
	const gap = 8;
	if (window.innerWidth - rect.right >= width + gap * 2) {
		uploadSortPos.value = { top: rect.top, left: rect.right + gap };
	} else if (rect.left >= width + gap * 2) {
		uploadSortPos.value = { top: rect.top, left: rect.left - width - gap };
	} else {
		// No room beside the picker (mobile): open under the sort button, inside the viewport.
		const left = Math.min(Math.max(rect.right - width, gap), window.innerWidth - width - gap);
		uploadSortPos.value = { top: rect.bottom + 4, left };
	}
};
const toggleUploadSort = () => {
	uploadSortOpen.value = !uploadSortOpen.value;
	if (uploadSortOpen.value) nextTick(placeUploadSort);
};
const setUploadSort = (value) => {
	uploadSort.value = value;
	localStorage.setItem(UPLOAD_SORT_KEY, value);
	uploadSortOpen.value = false;
	uploadSortChanged.value = value !== 'Newest';
};
const onUploadSortDocClick = (event) => {
	if (!uploadSortOpen.value) return;
	if (uploadSortRoot.value?.contains(event.target)) return;
	if (uploadSortMenu.value?.contains(event.target)) return;
	uploadSortOpen.value = false;
};
onMounted(() => window.addEventListener('click', onUploadSortDocClick));
onBeforeUnmount(() => window.removeEventListener('click', onUploadSortDocClick));
const onUploadToggle = (visible) => {
	if (!visible) {
		uploadSortOpen.value = false;
		return;
	}
	uploadSearch.value = '';
	nextTick(() => uploadSearchInput.value?.focus?.());
};
const onUploadSearchKeydown = (event) => {
	if (event.key !== 'Escape') return;
	event.preventDefault();
	if (uploadSortOpen.value) {
		uploadSortOpen.value = false;
		return;
	}
	if (uploadSearch.value) {
		uploadSearch.value = '';
		return;
	}
	uploadDropdown.value?.hide?.();
};
const pickUploadFolder = (folder) => {
	uploadDropdown.value?.hide?.();
	onNewVideo(folder, 'computer');
};

// Inline folder creation: popover anchored to the "New folder" button (no modal).
const newFolderMenu = ref(null);
const newFolderName = ref('');
const createProcessing = ref(false);
const newFolderInput = ref(null);
// Same limit as sub-folders (backend Joi max(120)) so naming rules stay consistent
const FOLDER_NAME_MAX = 120;
const newFolderNameTooLong = computed(() => newFolderName.value.trim().length > FOLDER_NAME_MAX);
const onNewFolderToggle = (visible) => {
	if (visible) {
		newFolderName.value = '';
		nextTick(() => newFolderInput.value?.focus?.());
	}
};
const closeNewFolderMenu = () => newFolderMenu.value?.hide?.();
const confirmCreateFolder = async () => {
	const name = newFolderName.value.trim();
	if (!name || newFolderNameTooLong.value || createProcessing.value) return;
	createProcessing.value = true;
	try {
		// Same flow as AddVodModal: VOD folders deploy into the vod region set.
		await store.dispatch('Regions/getRegions', 'vod');
		const regions = store.getters['Regions/getRegionsByCategory']('vod') || [];
		const regionId = regions[0]?._id;
		await StreamService.addStream(name, regionId, 'vod');
		await store.dispatch('Streams/getStreams', true);
		track(EVENTS.VOD_FOLDER_CREATED, { folderName: name });
		notify({ group: 'success', text: 'Folder created successfully' });
		newFolderName.value = '';
		closeNewFolderMenu();
		emit('refresh');
	} catch (err) {
		notify({ group: 'error', title: 'Couldn’t create folder', text: err?.message || '' });
	}
	createProcessing.value = false;
};

const matchesType = (f) => {
	if (typeFilter.value === 'all') return true;
	if (typeFilter.value === 'live') return !!f.liveParent;
	return !f.liveParent;
};

const sortedFolders = computed(() => {
	let list = props.folders.filter((f) => f?.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) && matchesType(f));
	switch (sortBy.value) {
		case 'Oldest':
			return _.orderBy(list, [(f) => new Date(f.creationTime)], ['asc']);
		case 'A to Z':
			return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['asc']);
		case 'Z to A':
			return _.orderBy(list, [(f) => (f.name || '').toLowerCase()], ['desc']);
		case 'Most videos':
			return _.orderBy(list, [(f) => f.totalFiles || 0, (f) => new Date(f.creationTime)], ['desc', 'desc']);
		case 'Newest':
		default:
			return _.orderBy(list, [(f) => new Date(f.creationTime)], ['desc']);
	}
});

const { recentIds, removeRecent, clearRecent } = useRecentFolders();
const onRemoveRecent = (folder) => removeRecent(folder?._id);
// Recent/Starred are scanning aids — with only a few folders they just
// duplicate what's already visible, so they only appear for larger libraries.
const showSections = computed(() => props.folders.length > 5);

// "Jump back in" row: last opened folders, in visit order. Deliberately
// ignores search/sort/type filters — it's navigation history, not a result set.
// Hidden while searching/filtering so it doesn't contradict the query.
const recentFolders = computed(() => {
	if (!showSections.value || searchQuery.value || typeFilter.value !== 'all') return [];
	return recentIds.value
		.map((id) => props.folders.find((f) => f._id === id))
		.filter(Boolean)
		.slice(0, 4);
});

const favoriteFolders = computed(() => showSections.value ? sortedFolders.value.filter((f) => isFavorite(f._id)) : []);
// Starred is a shortcut, not a move: starred folders stay in the main list too
// (Drive/Vimeo convention), so the main list always matches its sort order.
const otherFolders = computed(() => sortedFolders.value);

// Pagination (applies to the main, non-starred folder list)
// Compact cards fit many more folders per screen; paginate only for large libraries.
const PAGE_SIZE = 24;
const currentPage = ref(0);
const totalPages = computed(() => Math.ceil(otherFolders.value.length / PAGE_SIZE));
const paginatedFolders = computed(() =>
	otherFolders.value.slice(currentPage.value * PAGE_SIZE, currentPage.value * PAGE_SIZE + PAGE_SIZE));
const goToPage = (page) => { currentPage.value = Math.min(Math.max(0, page), Math.max(0, totalPages.value - 1)); };
// Reset / clamp the page when the filtered result set changes.
watch([searchQuery, sortBy, typeFilter], () => { currentPage.value = 0; });
watch(totalPages, (pages) => { if (currentPage.value > pages - 1) currentPage.value = Math.max(0, pages - 1); });

const formatBytes = (size) => {
	if (!size || size <= 0) return '0 GB';
	const i = Math.floor(Math.log(size) / Math.log(1000));
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
	return `${(size / Math.pow(1000, i)).toFixed(1) * 1} ${sizes[i]}`;
};

const storageText = computed(() => {
	const total = props.maxStorageGb ? `${props.maxStorageGb} GB` : '—';
	return `${formatBytes(props.storageUsedBytes)} of ${total}`;
});


// Inline rename from the card (no modal) — same flow as RenameVODFolderModal
const onRename = async (folder, newName) => {
	const name = (newName || '').trim();
	if (!name || name === folder?.name) return;
	try {
		await StreamService.setStreamName(folder._id, name);
		PaywallService.updateStreamPaywall(folder._id, { title: name }).catch((err) => {
			console.error('update paywall stream name failed: ', err);
		});
		notify({ group: 'success', title: 'Folder name updated' });
		await store.dispatch('Streams/getStreams', true);
		emit('refresh');
	} catch (err) {
		notify({ group: 'error', title: 'Couldn’t rename folder', text: err.message });
	}
};

// Deletion is confirmed inline on the card (trash → Cancel/Remove). The card is
// removed optimistically; on failure the list is re-synced from the server.
const onDelete = async (folder) => {
	if (!folder) return;
	store.dispatch('Streams/removeStream', folder._id);
	const streamId = folder.liveParent ? folder.liveParent : folder._id;
	try {
		// Clean up exported DVR archives for live-to-vod folders
		let episodes = [];
		try {
			episodes = await StreamService.getStreamDvrArchives(streamId);
		} catch (e) { /* ignore */ }
		const permanent = _.filter(episodes, { exportStatus: 'EXPORTED' }) || [];
		for (const video of permanent) {
			if (folder.liveParent || !video.pseudoVod) {
				await StreamService.deleteStreamDvrArchive({
					streamId,
					from: video.from,
					duration: video.duration,
					abr: video.abr,
					namedHls: video.namedHls,
				});
			}
		}
		await StreamService.deleteStream(folder._id);
		track(EVENTS.VOD_FOLDER_DELETED, {});
		notify({ group: 'success', text: `"${folder.name}" was deleted` });
	} catch (err) {
		notify({ group: 'error', title: err?.error, text: err?.message || 'Could not delete folder' });
		await store.dispatch('Streams/getStreams', true);
	}
};
</script>

<template>
	<div>
		<!-- Header -->
		<PageHeader class="mb-3 md:mb-0" title="Content Library" fit-meta-to-right subtitle="Host and manage your on-demand video library." meta-mobile-only>
			<template #meta>
				<VODStorageStat v-if="!smUp" />
			</template>
			<div class="flex w-full sm:w-auto items-center gap-2 sm:gap-3 max-sm:flex-nowrap [&>*]:min-w-0 [&>*]:basis-[calc(50%-0.25rem)] sm:[&>*]:basis-auto">
				<VODStorageStat v-if="smUp" />
				<!-- New folder: inline popover anchored to the button -->
				<DropDownMenu ref="newFolderMenu" position="auto-end" :disabled="disabled || !canManage"
				type="outline"
					class="max-sm:w-full max-sm:[&>button]:!w-full max-sm:[&>button]:!justify-center"
					customDropdownClass="!min-w-[280px]" @toggle="onNewFolderToggle">
					<template #toggle-button>
						<icon-base class="!mr-1">
							<icon-plus />
						</icon-base>
						New folder
					</template>
					<template #menu-items>
						<li class="p-3" @click.stop>
							<label class="block text-tiny text-surface-8 mb-1.5">Folder name</label>
							<FormInput ref="newFolderInput" v-model="newFolderName" placeholder="e.g. Product demos"
								@keyup.enter="confirmCreateFolder" @keyup.esc="closeNewFolderMenu" />
							<p v-if="newFolderNameTooLong" class="text-tiny text-error mt-1.5 mb-0">
								Folder name can’t exceed {{ FOLDER_NAME_MAX }} characters ({{ newFolderName.trim().length }}/{{ FOLDER_NAME_MAX }}).
							</p>
							<div class="flex items-center justify-end gap-1.5 mt-2.5">
								<FormButton type="secondary" size="sm" @click="closeNewFolderMenu">Cancel</FormButton>
								<FormButton type="main" size="sm" :disabled="!newFolderName.trim() || newFolderNameTooLong || createProcessing"
									@click="confirmCreateFolder">
									{{ createProcessing ? 'Creating…' : 'Create' }}
								</FormButton>
							</div>
						</li>
					</template>
				</DropDownMenu>
				<!-- Single folder: upload goes straight there. Multiple: anchored picker. -->
				<FormButton v-if="uploadTargets.length === 1" type="main" size="md" class="max-sm:!w-full max-sm:!justify-center" :disabled="disabled || !canManage" @click="onUploadClick">
					<icon-base class="!mr-1" fill="none">
						<icon-upload />
					</icon-base>
					Upload video
				</FormButton>
				<DropDownMenu v-else-if="uploadTargets.length" ref="uploadDropdown" position="right" :disabled="disabled || !canManage"
					type="main"
					class="max-sm:w-full max-sm:[&>button]:!w-full max-sm:[&>button]:!justify-center"
					customDropdownClass="!w-[300px] !max-w-[calc(100vw-2rem)]" listClasses="!max-h-[360px]"
					@toggle="onUploadToggle">
					<template #toggle-button>
						<icon-base class="!mr-1" fill="none">
							<icon-upload />
						</icon-base>
						Upload video
					</template>
					<template #menu-items>
						<li class="sticky -top-1 z-20 -mt-1 bg-surface-2" @click.stop>
							<p class="px-3 pt-2.5 pb-1 mb-0 text-tiny text-surface-7">Upload to</p>
							<div ref="uploadSortRoot" class="px-2 pt-1 pb-1.5">
							<div class="relative flex items-center">
								<icon-base class="absolute left-2.5 text-surface-8 pointer-events-none z-10" width="14" height="14" fill="none">
									<icon-search />
								</icon-base>
								<FormInput
									ref="uploadSearchInput"
									v-model="uploadSearch"
									type="search"
									size="sm"
									placeholder="Search folder"
									:customClasses="uploadSearch ? '!pl-8 !pr-14 !bg-surface-3' : '!pl-8 !pr-8 !bg-surface-3'"
									enterkeyhint="search"
									@keydown="onUploadSearchKeydown"
									@focus="uploadSortOpen = false"
								/>
								<FormButton v-if="uploadSearch" aria-label="Clear search" title="Clear"
									custom-classes="absolute right-7 z-10 inline-flex items-center justify-center size-5 border-0 bg-transparent p-0 text-surface-8 hover:text-white"
									@click.stop="uploadSearch = ''">
									<icon-base width="12" height="12" fill="transparent">
										<icon-cross />
									</icon-base>
								</FormButton>
								<button
									type="button"
									class="absolute right-1 z-10 inline-flex size-6 items-center justify-center rounded text-surface-7 hover:text-white"
									:class="{ 'text-white': uploadSortOpen }"
									aria-label="Sort folders"
									aria-haspopup="menu"
									:aria-expanded="uploadSortOpen"
									@click.stop="toggleUploadSort"
								>
									<icon-base width="14" height="14" fill="none">
										<icon-sorting />
									</icon-base>
									<span
										v-if="uploadSortChanged"
										class="pointer-events-none absolute top-0.5 right-0.5 size-1.5 rounded-full bg-error"
										aria-hidden="true"
									/>
								</button>
							</div>
							</div>
						</li>
						<li v-if="uploadSearch.trim() && !filteredUploadTargets.length" class="px-3 py-4 text-center text-tiny text-surface-7">
							<p class="m-0">No result for “{{ uploadSearch.trim() }}”</p>
							<p class="m-0">Please try searching with another keyword.</p>
						</li>
						<DropDownMenuItem v-for="folder in filteredUploadTargets" :key="'pick-' + folder._id" isCustom>
							<FormButton
								custom-classes="flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left hover:bg-surface-3 transition"
								@click.stop="pickUploadFolder(folder)">
								<div class="size-7 shrink-0 rounded-md flex items-center justify-center"
									:style="{ background: folderTint(folder).bg, color: folderTint(folder).fg }">
									<span class="text-tiny font-semibold select-none">{{ folderInitial(folder) }}</span>
								</div>
								<div class="min-w-0 flex-1">
									<p class="text-tiny text-white font-medium truncate m-0" :title="folder.name">{{ folder.name }}</p>
									<p class="text-[11px] text-surface-7 m-0 leading-tight">{{ folder.totalFiles || 0 }} {{ (folder.totalFiles || 0) === 1 ? 'video' : 'videos' }}</p>
								</div>
							</FormButton>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
				<Teleport to="body">
					<div
						v-if="uploadSortOpen"
						ref="uploadSortMenu"
						role="menu"
						class="fixed z-50 w-[168px] rounded-md bg-surface-3 py-1 shadow-dropdown"
						:style="{ top: `${uploadSortPos.top}px`, left: `${uploadSortPos.left}px` }"
						@click.stop
						@mousedown.prevent
					>
						<p class="px-3 pt-1.5 pb-1 mb-0 text-tiny text-surface-7">Sort by</p>
						<button
							v-for="opt in uploadSortOptions"
							:key="opt"
							type="button"
							role="menuitemradio"
							:aria-checked="uploadSort === opt"
							class="flex w-full items-center justify-between gap-3 h-8 px-3 text-left text-tiny text-surface-text-1 hover:bg-surface-4 hover:text-white"
							@click="setUploadSort(opt)"
						>
							<span>{{ opt }}</span>
							<icon-base v-if="uploadSort === opt" class="shrink-0 text-surface-7" fill="none">
								<icon-checkmark />
							</icon-base>
						</button>
					</div>
				</Teleport>
			</div>
		</PageHeader>

		<!-- Toolbar: browsing controls only earn their row once the library is big
		     enough to need them (small libraries get a clean, chrome-free page) -->
		<ResponsiveToolbar v-if="showListControls" class="mb-6" spacious>
			<template #leading>
				<TabGroup type="custom" custom-classes="flex max-w-full items-center overflow-x-auto rounded-md bg-surface-2 p-0.5 ring-1 ring-surface-3 w-full md:w-auto">
					<TabItem v-for="tab in typeTabs" :key="tab.value" type="custom" @click="changeType(tab.value)"
						:custom-classes="'h-7 px-3 rounded flex items-center gap-1.5 text-tiny transition whitespace-nowrap ' + (typeFilter === tab.value ? 'bg-surface-4 text-white font-medium' : 'text-surface-8 hover:text-white')" class="basis-1/3 md:basis-auto">
						{{ tab.label }}
						<span class="tabular-nums" :class="typeFilter === tab.value ? 'text-surface-9' : 'text-surface-6'">{{ tab.count }}</span>
					</TabItem>
				</TabGroup>
			</template>

			<DropDownMenu ref="sortDropdown" type="secondary" position="left">
				<template #toggle-button>
					<span class="text-surface-8">Sort by:&nbsp;</span>
					<span class="text-white">{{ sortBy }}</span>
					<icon-base class="!ml-1 shrink-0 text-surface-8">
						<icon-chevron-up-down />
					</icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem v-for="opt in sortOptions" :key="opt" :active="sortBy === opt" @click="changeSort(opt)">
						{{ opt }}
						<icon-base v-if="sortBy === opt" class="shrink-0 !ml-2 text-surface-7" fill="none">
							<icon-checkmark />
						</icon-base>
					</DropDownMenuItem>
				</template>
			</DropDownMenu>

			<ListingSearchInput v-model="searchQuery" />

			<div class="view-mode-switcher flex items-center bg-surface-2 rounded-md p-0.5 ring-1 ring-surface-3 shrink-0">
				<FormButton :custom-classes="['size-7 rounded flex items-center justify-center transition',
					view === 'card' ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white']"
					@click="setView('card')" aria-label="Card view">
					<icon-base fill="currentColor">
						<icon-grid />
					</icon-base>
				</FormButton>
				<FormButton :custom-classes="['size-7 rounded flex items-center justify-center transition',
					view === 'list' ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white']"
					@click="setView('list')" aria-label="List view">
					<icon-base fill="currentColor">
						<icon-list />
					</icon-base>
				</FormButton>
			</div>
		</ResponsiveToolbar>

		<VODReachLimitBanner />

		<!-- Recents: jump back into the folders you last opened -->
		<template v-if="recentFolders.length">
			<div class="flex items-center justify-between mb-3">
				<h2 class="text-sm font-medium text-surface-9">Recent</h2>
				<FormButton custom-classes="text-tiny text-surface-8 hover:text-white transition" @click="clearRecent">
					Clear
				</FormButton>
			</div>
			<div v-if="view === 'card'"
				class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 mb-8">
				<VODFolderCard v-for="folder in recentFolders" :key="'recent-' + folder._id" :folder="folder" view="card"
					recent @removeRecent="onRemoveRecent"
					@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
			</div>
			<div v-else class="flex w-full min-w-0 flex-col gap-2 mb-8 [contain:inline-size]">
				<VODFolderCard v-for="folder in recentFolders" :key="'recent-' + folder._id" :folder="folder" view="list"
					recent @removeRecent="onRemoveRecent"
					@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
			</div>
		</template>

		<!-- Favorite section -->
		<template v-if="favoriteFolders.length">
			<h2 class="text-sm font-medium text-surface-9 mb-3">Starred</h2>
			<div v-if="view === 'card'"
				class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 mb-8">
				<VODFolderCard v-for="folder in favoriteFolders" :key="folder._id" :folder="folder" view="card"
					@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
			</div>
			<div v-else class="flex w-full min-w-0 flex-col gap-2 mb-8 [contain:inline-size]">
				<VODFolderCard v-for="folder in favoriteFolders" :key="folder._id" :folder="folder" view="list"
					@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
			</div>
		</template>

		<!-- All folders -->
		<h2 v-if="(recentFolders.length || favoriteFolders.length) && otherFolders.length"
			class="text-sm font-medium text-surface-9 mb-3">All folders</h2>
		<div v-if="view === 'card'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
			<VODFolderCard v-for="folder in paginatedFolders" :key="folder._id" :folder="folder" view="card"
				@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
		</div>
		<div v-else class="flex w-full min-w-0 flex-col gap-2 [contain:inline-size]">
			<div class="flex items-center gap-3 px-3 text-tiny uppercase tracking-wider text-surface-6">
				<span class="w-6 shrink-0"></span>
				<span class="w-9 shrink-0"></span>
				<span class="flex-1 min-w-0">Name</span>
				<span class="hidden md:block w-[130px] shrink-0 text-left">Type</span>
				<span class="hidden sm:block w-[70px] shrink-0 text-right">Videos</span>
				<span class="hidden sm:block w-[80px] shrink-0 text-right">Size</span>
				<span class="hidden lg:block w-[90px] shrink-0 text-right">Updated</span>
				<span class="w-14 shrink-0"></span>
			</div>
			<VODFolderCard v-for="folder in paginatedFolders" :key="folder._id" :folder="folder" view="list"
				@newVideo="onNewVideo" @rename="onRename" @delete="onDelete" />
		</div>

		<Pagination v-if="totalPages > 1" class="mt-6 w-fit mr-auto" :current-page="currentPage" :pages="totalPages"
			@go-to-page="goToPage" />

		<div v-if="sortedFolders.length === 0" class="flex flex-col items-center text-center mt-16">
			<p class="text-sm font-medium text-surface-9 mb-1">No folders match</p>
			<p class="text-tiny text-surface-7 mb-4">
				<template v-if="searchQuery">Nothing named “{{ searchQuery }}”<template v-if="typeFilter !== 'all'"> in this filter</template>.</template>
				<template v-else>No folders of this type yet.</template>
			</p>
			<FormButton v-if="searchQuery || typeFilter !== 'all'" type="secondary" size="sm"
				@click="searchQuery = ''; typeFilter = 'all'">
				Clear filters
			</FormButton>
		</div>

	</div>
</template>
